import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  createAttributeCatalog,
  CreateAttributeCatalogRequest,
  getAttributeCatalog,
  updateAttributeCatalog,
  UpdateAttributeCatalogRequest
} from "src/network/graphql/transactionService";
import { AttributeCatalogType } from "src/types/commonTypes";

export interface AccountingCodeItem {
  attribute_catalog_id: string;
  code: string;
  type: AttributeCatalogType;
  title: string;
  status: string;
  split_transactions?: boolean;
  display_name?: string;
  hidden?: boolean;
  organisation_id?: string;
}

interface AccountingCodesState {
  loading: boolean;
  error: unknown;
  items: AccountingCodeItem[];
  total: number;
  saving: boolean;
}

const initialState: AccountingCodesState = {
  loading: false,
  error: null,
  items: [],
  total: 0,
  saving: false
};

const accountingCodesSlice = createSlice({
  name: "accountingCodes",
  initialState,
  reducers: {
    fetchCodesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCodesSuccess: (state, action: PayloadAction<{ items: AccountingCodeItem[]; total: number }>) => {
      state.loading = false;
      state.items = action.payload.items;
      state.total = action.payload.total;
    },
    fetchCodesFail: (state, action) => {
      state.loading = false;
      state.items = [];
      state.total = 0;
      state.error = action.payload;
    },
    saveStart: (state) => {
      state.saving = true;
    },
    saveFinish: (state) => {
      state.saving = false;
    }
  }
});

export const { fetchCodesStart, fetchCodesSuccess, fetchCodesFail, saveStart, saveFinish } =
  accountingCodesSlice.actions;

export interface FetchAccountingCodesParams {
  type: AttributeCatalogType;
  from: number;
  size: number;
  code?: string;
}

export const fetchAccountingCodesAction = ({ type, from, size, code }: FetchAccountingCodesParams) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchCodesStart());
    try {
      const response = await getAttributeCatalog({
        from,
        size,
        attribute_catalog_filter: {
          type,
          ...(code?.trim() ? { code: code.trim() } : {})
        }
      });
      const result = response.data?.search_attribute_catalog;

      dispatch(
        fetchCodesSuccess({
          items: result?.items ?? [],
          total: result?.total_size ?? 0
        })
      );
      return result?.items ?? [];
    } catch (error) {
      dispatch(fetchCodesFail(error));
      return null;
    }
  };
};

export const createAccountingCodeAction = (data: CreateAttributeCatalogRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await createAttributeCatalog(data);
      const created = response.data?.create_attribute_catalog;

      if (!created) {
        throw new Error("Create attribute catalog failed");
      }
      immediateToast("success", { message: "Code created successfully", timeout: 3000, position: "topCenter" });
      dispatch(saveFinish());
      if (cb) cb();
      return created;
    } catch (error) {
      immediateToast("error", { message: "Failed to create code", timeout: 3000, position: "topCenter" });
      dispatch(saveFinish());
      return null;
    }
  };
};

export const updateAccountingCodeAction = (data: UpdateAttributeCatalogRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await updateAttributeCatalog(data);
      const updated = response.data?.update_attribute_catalog;

      if (!updated) {
        throw new Error("Update attribute catalog failed");
      }
      immediateToast("success", { message: "Code updated successfully", timeout: 3000, position: "topCenter" });
      dispatch(saveFinish());
      if (cb) cb();
      return updated;
    } catch (error) {
      immediateToast("error", { message: "Failed to update code", timeout: 3000, position: "topCenter" });
      dispatch(saveFinish());
      return null;
    }
  };
};

export default accountingCodesSlice.reducer;
